import React, { useEffect, useState } from 'react';
import { api } from '../api';
import './AuditTrail.css';

interface AuditEntry {
  id: number;
  expense_id: string;
  user_id: number;
  action: string;
  changed_by_id: number;
  changed_by_email: string;
  changed_by_role: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  change_summary: string | null;
  created_at: string;
}

interface AuditTrailProps {
  expenseId: string;
  token: string;
}

const AuditTrail: React.FC<AuditTrailProps> = ({ expenseId, token }) => {
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAuditLog = async () => {
      try {
        setLoading(true);
        console.log('[AuditTrail] Fetching audit log for expense:', expenseId);
        const response = await api.get(`/api/expenses/${expenseId}/audit`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log('[AuditTrail] Received audit log:', response.data);
        setAuditLog(response.data.audit_log || []);
        setError(null);
      } catch (err: any) {
        console.error('[AuditTrail] Failed to fetch audit log:', err);
        console.error('[AuditTrail] Error details:', err.response?.data);
        setError(err.response?.data?.message || 'Failed to load audit trail');
      } finally {
        setLoading(false);
      }
    };

    if (expenseId && token) {
      fetchAuditLog();
    }
  }, [expenseId, token]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return '➕';
      case 'updated':
      case 'admin_edit':
        return '✏️';
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      case 'status_changed':
        return '🔄';
      case 'fx_override':
        return '💱';
      default:
        return '📝';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return '#10b981';
      case 'updated':
      case 'admin_edit':
        return '#3b82f6';
      case 'approved':
        return '#059669';
      case 'rejected':
        return '#dc2626';
      case 'status_changed':
        return '#f59e0b';
      case 'fx_override':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  const formatFieldName = (fieldName: string | null) => {
    if (!fieldName) return '';
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value: string | null, fieldName: string | null) => {
    if (!value) return 'none';
    
    // Format dates nicely
    if (fieldName === 'date' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    
    // Format currency amounts
    if ((fieldName === 'amount' || fieldName === 'reimbursement_amount' || fieldName === 'vat_amount') && !isNaN(Number(value))) {
      return Number(value).toFixed(2);
    }
    
    return value;
  };

  const getChangeDescription = (entry: AuditEntry) => {
    const role = entry.changed_by_role === 'super_admin' || entry.changed_by_role === 'partner' ? 'Admin' : 'User';
    
    // Status changes
    if (entry.action === 'approved') {
      return `${role} approved expense`;
    }
    if (entry.action === 'rejected') {
      return `${role} rejected expense`;
    }
    if (entry.action === 'status_changed' && entry.field_name === 'status') {
      return `${role} changed status from ${formatValue(entry.old_value, 'status')} to ${formatValue(entry.new_value, 'status')}`;
    }
    
    // Field changes
    if (entry.field_name && entry.old_value !== null && entry.new_value !== null) {
      const fieldLabel = formatFieldName(entry.field_name).toLowerCase();
      const oldVal = formatValue(entry.old_value, entry.field_name);
      const newVal = formatValue(entry.new_value, entry.field_name);
      return `${role} changed ${fieldLabel} from ${oldVal} to ${newVal}`;
    }

    if (entry.action === 'created') {
      return 'Expense created';
    }
    
    if (entry.action === 'admin_edit') {
      return `${role} edited expense`;
    }

    return entry.change_summary || entry.action.charAt(0).toUpperCase() + entry.action.slice(1);
  };

  if (loading) {
    return (
      <div className="audit-trail-loading">
        <div className="spinner"></div>
        <span>Loading audit trail...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audit-trail-error">
        <span>⚠️ {error}</span>
      </div>
    );
  }

  if (auditLog.length === 0) {
    return (
      <div className="audit-trail-empty">
        <span>📋 No audit trail available</span>
      </div>
    );
  }

  return (
    <div className="audit-trail">
      <div className="audit-trail-header">
        <h3>📜 Audit Trail</h3>
        <span className="audit-trail-count">{auditLog.length} {auditLog.length === 1 ? 'entry' : 'entries'}</span>
      </div>
      
      <div className="audit-trail-timeline">
        {auditLog.map((entry, index) => (
          <div key={entry.id} className="audit-trail-entry audit-trail-entry-compact">
            <div className="audit-trail-entry-icon" style={{ backgroundColor: getActionColor(entry.action) }}>
              {getActionIcon(entry.action)}
            </div>
            
            <div className="audit-trail-entry-content">
              <div className="audit-trail-entry-description">
                {getChangeDescription(entry)}
              </div>
              
              <div className="audit-trail-entry-meta">
                <span className="audit-trail-entry-user-email">{entry.changed_by_email}</span>
                <span className="audit-trail-entry-time">{formatDate(entry.created_at)}</span>
              </div>
            </div>
            
            {index < auditLog.length - 1 && <div className="audit-trail-entry-line"></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditTrail;
